import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import ImageGallery from 'react-image-gallery';
import { css } from 'emotion';
import { Button } from 'antd';
import { DraggableModal, DraggableModalProvider, DraggableModalContext } from 'ant-design-draggable-modal';
import 'antd/dist/antd.css';
import 'ant-design-draggable-modal/dist/index.css';
import $ from 'jquery';
import LoadingSpinner from 'components/core/LoadingSpinner';
import { colorGreen, getAnchorStyle } from 'styles/base.js';

const baseUrl = process.env.REACT_APP_BASE_URL; // this set via webpack build during transpile

/**
 * We are wrapping the image gallery inside a functional component so that we can get access to
 * the draggable modal context so we can figure out the correct height and width!
 * @returns {*}
 * @constructor
 */
const ImageGalleryWrapper = (props) => {

    // Get modal provider.
    const modalProvider = useContext(DraggableModalContext);
    const { dispatch, state } = modalProvider;

    // DraggableModal state holds multiple modal states in a map, the id's of which are randomly generated,
    // so we just get the first one
    const modalId = Object.keys(state.modals)[0];
    const modalState = state.modals[modalId]; // note this has the size of the entire modal, including header/footer

    let component = <div/>;

    if (!props.userLoggedIn) {
        component = <div>You must be logged in to view data consolidator results.</div>;

    } else {
        const wrapperStyle = css`
            width: ${modalState.width}px;
            height: ${modalState.height - 150}px;
            .image-gallery-content {
                width: 100%;
                display: flex;
                flex-direction: column;
                .image-gallery-slide-wrapper {
                    display: flex;
                    flex-direction: column;    
                    .image-gallery-slide {
                        display: flex;
                        justify-content: center;
                        .image-gallery-image {
                            width: 90%;
                        }
                    }            
                }
                .image-gallery-play-button, .image-gallery-fullscreen-button {
                    position: relative;
                    align-self: flex-end;
                }
                .image-gallery-play-button::before, .image-gallery-fullscreen-button::before {
                    color: ${colorGreen}; 
                    padding: 0;                
                }
                .image-gallery-play-button:hover::before, .image-gallery-fullscreen-button:hover::before {
                    color: ${colorGreen};
                }
                
                .image-gallery-left-nav::before, .image-gallery-right-nav::before {
                    color: ${colorGreen};                 
                }
                .image-gallery-left-nav:hover::before, .image-gallery-right-nav:hover::before {
                    color: ${colorGreen};
                }                 
            }
        `;

        if (props.status === 'Success') {
            component = (
                <div className={wrapperStyle}>
                    <ImageGallery items={props.plots}
                        lazyLoad
                        showThumbnails
                        thumbnailPosition="bottom"
                        showPlayButton={false}
                        showFullscreenButton
                        slideInterval={2000}
                    />
                </div>
            );

        } else if (props.status === 'Error') {
            component = <div>An error occured generating plots.  See server for more details.</div>;

        } else {
            component = <LoadingSpinner/>;
        }
    }

    return component;
};

/**
 * Renders a modal image gallery for plots generated for one data consolidator run.
 */
export default class DataConsolidatorPlotViewer extends React.Component {
    static propTypes = {
        onClose: PropTypes.func,                 // close handler
        process: PropTypes.object,               // process
        userLoggedIn: PropTypes.bool,
        dqPlot: PropTypes.bool,
        runDate: PropTypes.string.isRequired     // specific run date of the run to plot
    };

    constructor(props) {
        super(props);

        this.state = {
            plots: null,
            status: null
        };

        this.firstLoad = true;
    }

    componentDidMount() {
        this.intervalId = setInterval(this.fetchPlotsIfNeeded, 1000);
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    stopPolling = () => {
        clearInterval(this.intervalId);
    };

    fetchPlotsIfNeeded = () => {

        // Only fetch if results haven't been loaded yet
        if (this.state.status !== 'Error' && this.state.status !== 'Success' && !this.fetching) {
            this.fetching = true;

            // Load the plots via remote call.  I don't think we need to use the store, as this won't be shared
            // with any other views.
            const sessionId = localStorage.getItem('sessionId');
            let url = `${baseUrl}/processes/${this.props.process.name}/getplots/${this.props.runDate}?session_id=${sessionId}`;
            let plots = null;
            let status = null;

            if (this.props.dqPlot) {
                url = `${url}&dq=true`;
            }

            if (this.firstLoad) {
                this.firstLoad = false;
                url = `${url}&reload=true`;
            }

            $.ajax({
                url

            }).done((data, textStatus, jqXHR) => { // request was sucessful
                status = data.status;

                if (status === 'Success') {
                    plots = data.images;
                    this.stopPolling();

                } else if (status === 'Error') {
                    this.stopPolling();
                }
                this.setState({ plots, status });

            }).fail((jqXHR, textStatus, errorThrown) => {
                status = 'Error';
                this.stopPolling();
                this.setState({ plots, status });

            }).always(() => {
                this.fetching = false;
            });

        }
    };

    getPlots() {
        let plots = [];

        if (this.state.status === 'Success') {
            const sessionId = localStorage.getItem('sessionId');
            const runDate = this.props.runDate;

            const imageNameArray = this.state.plots;
            plots = imageNameArray.map(imageName => {
                let url = `${baseUrl}/processes/${this.props.process.name}/getplot/${runDate}/${imageName}?session_id=${sessionId}`;
                if (this.props.dqPlot) {
                    url = `${url}&dq=true`;
                }
                const imageUrl = url;
                const thumbUrl = `${url}&thumb=true`;
                return {
                    original: imageUrl,
                    thumbnail: thumbUrl
                };
            });
        }

        return plots;
    }

    getButtonStyle() {
        const style = css`
            ${getAnchorStyle(colorGreen)}
            padding: 0 5px;
        `;
        return style;
    }

    renderPlayPauseButton = (onClick, isPlaying) => {
        const style = css`
            ${this.getButtonStyle()}
            align-self: flex-end;
        `;

        let icon = null;
        if (isPlaying) {
            icon = <i className="fa fa-2x fa-pause" aria-hidden="true" />;
        } else {
            icon = <i className="fa fa-2x fa-play" aria-hidden="true" />;
        }
        return <a className={style} onClick={onClick}>{icon}</a>;
    };

    renderFullscreenButton = (onClick, isFullscreen) => {
        const style = this.getButtonStyle();

        let icon = null;
        if (isFullscreen) {
            icon = <i className="fa fa-2x fa-collapse" aria-hidden="true" />;

        } else {
            icon = <i className="fa fa-2x fa-expand" aria-hidden="true" />;
        }
        return <a className={style} onClick={onClick}>{icon}</a>;
    };

    render() {
        const modalStyle = css`
            .ant-modal-content {
                max-width: calc(100vw);
            }
            .ant-modal-body {
                padding: 12px;
            }
        `;

        return (
            <DraggableModalProvider>
                <DraggableModal
                    zIndex={1002}
                    className={modalStyle}
                    title="Plot Viewer"
                    visible
                    onOk={this.props.onClose}
                    onCancel={this.props.onClose}
                    footer={[
                        <Button key="close" onClick={this.props.onClose}>Close</Button>
                    ]}
                >
                    <ImageGalleryWrapper userLoggedIn={this.props.userLoggedIn} status={this.state.status} plots={this.getPlots()}/>
                </DraggableModal>
            </DraggableModalProvider>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/core/DataConsolidatorPlotViewer.js